// M3 nationalization, participation and service contracts (M3_SPEC §7.3) and the scripted region events of the era
// (§16: Biafra and Kuwait occupations shut production in, the 1991 Kuwait well fires).
//   seize           → the M2 seizure path (sim/m2/politics.js seizeRegion; closures now include M3 'seize' records)
//   participation   → the state takes `share` of each barrel produced and of operating cost; compensation × book value
//                     of the increment; the player may buy the state's crude back at spot × (1 − buybackDiscount)
//   serviceContract → the state owns the oil; the player keeps operating for `feePerBbl` (base-year $) per barrel
// Equity is per well (Norway's carried share applies to licences granted from 1972 only), blended by today's rates.
import { wellsOnField } from '../indexes.js';
import { NATIONALIZATIONS, nationalizationsAt } from '../../data/m3/regions.js';
import { yearFrac } from '../../core/time.js';
import { chance, int, range } from '../../core/rng.js';
import { eraDollars, fieldDef, regionDef, yf } from '../defs.js';
import { spotPrice } from '../../economy/market.js';
import { politicsState, regionState, bookValue, seizeRegion, fieldsInRegion, hasAssets } from '../m2/politics.js';
import { queueDecision, popDecision } from '../security.js';
import { notify, spend, earn, book, fmt$ } from '../util.js';
import { econState, isUsCompany, bumpStat, m3On } from './econ.js';
import { m4WellNat, M4_NAT_REGIONS } from '../m4/worldHooks.js'; // M4-C

export const PARTICIPATION_COMP_DAYS = 180;
export const SERVICE_COMP_DAYS = 365;
export const OCCUPATION_SHUTIN_MAX_DAYS = 400; // Biafra: exports from Bonny resumed in 1968-69, not after 925 days
export const FIRE_CAP_COST = 1.5e6;            // 1991 $ per well (Red Adair, Boots & Coots ~ $1.5B for ~700 wells)
export const FIRE_CAP_BASE_YEAR = 1991;

const opts = (state, licenceYear = null) => ({ usCompany: isUsCompany(state), licenceYear });
const NAT_REGIONS = new Set(NATIONALIZATIONS.map((n) => n.regionId));
const natById = (id) => NATIONALIZATIONS.find((n) => n.id === id) ?? null;

// Regime for one well (or a region with no licence year): { mode: 'none'|'participation'|'service'|'seize', share, rec }.
export function natStatus(state, rid, licenceYear = null) {
  const y = yf(state);
  if (y < 1946 || !rid) return { mode: 'none', share: 0, rec: null };
  const recs = nationalizationsAt(rid, y, opts(state, licenceYear));
  const seize = recs.find((r) => r.mode === 'seize');
  if (seize) return { mode: 'seize', share: 1, rec: seize };
  const svc = recs.find((r) => r.mode === 'serviceContract');
  if (svc) return { mode: 'service', share: 1, rec: svc };
  let best = null;
  for (const r of recs) if (r.mode === 'participation' && (!best || r.share > best.share)) best = r;
  return best ? { mode: 'participation', share: best.share, rec: best } : { mode: 'none', share: 0, rec: null };
}

export function wellLicenceYear(state, w) {
  const p = state.fields[w.fieldId]?.plots?.[w.plot];
  const d = p?.leaseDay ?? w.spudDay ?? w.prod?.startDay;
  return d != null ? yearFrac(d) : null;
}
export function wellNat(state, w) {
  const m4 = state.m4w ? m4WellNat(state, w) : null; // M4-C: Venezuela 2007 mixed companies, Kashagan 2008 (null before 2000)
  if (m4) return m4;
  const s = natStatus(state, fieldDef(w.fieldId)?.regionId, wellLicenceYear(state, w));
  // UK 5th round (BNOC 51%): a licence-level state partner share
  const sp = state.fields[w.fieldId]?.plots?.[w.plot]?.licence?.statePartnerShare ?? 0;
  if (sp > 0 && (s.mode === 'none' || (s.mode === 'participation' && s.share < sp))) return { mode: 'participation', share: sp, rec: { id: 'licence_partner', buybackDiscount: 0 } };
  return s;
}

// Fee per barrel under a service contract (era $).
export const serviceFee = (state, rec) => eraDollars(state, rec.feePerBbl ?? 0, rec.baseYear ?? 1975);

// production.tickProduction, before storeOil: split today's field output q into the player's equity barrels.
// Service-contract barrels earn the fee here; the state carries its share of operating cost (opex refunded); the
// state's participation barrels may be bought back (setBuyback). `nbFn` = production.netback (passed in: no cycle).
export function equityBbl(state, fieldId, q, opexPerWell = 0, nbFn = null) {
  if (!(q > 0) || !m3On(state)) return q;
  const rid = fieldDef(fieldId)?.regionId;
  if (!rid || (!NAT_REGIONS.has(rid) && !(state.m4w && M4_NAT_REGIONS.has(rid)) && !state.fields[fieldId]?.plots?.some((p) => p.licence?.statePartnerShare))) return q; // M4-C: + M4 participation regions
  let tot = 0, eq = 0, fee = 0, stateBbl = 0, stateWells = 0, buyDisc = 0;
  for (const w of wellsOnField(state, fieldId)) { // perf: field index
    if (w.status !== 'PRODUCING' || !(w.prod?.rate > 0) || w.ownerOperatorId) continue;
    const r = w.prod.rate;
    const s = wellNat(state, w);
    tot += r;
    if (s.mode === 'service') fee += r * serviceFee(state, s.rec);
    else if (s.mode === 'participation') { eq += r * (1 - s.share); stateBbl += r * s.share; stateWells += s.share; buyDisc = Math.max(buyDisc, s.rec.buybackDiscount ?? 0); }
    else if (s.mode === 'none') eq += r;
  }
  if (!(tot > 0)) return q;
  const k = q / tot; // today's stored barrels vs the wells' rates (equal unless capped downstream)
  if (fee > 0) { earn(state, fee * k, 'revenue'); state.company.stats.revenueTotal += fee * k; }
  if (stateWells > 0 && opexPerWell > 0) { const amt = stateWells * opexPerWell; state.company.cash += amt; book(state, 'opex', -amt); }
  if (stateBbl > 0 && econState(state).nat.buyback[rid] && nbFn) buyBack(state, fieldId, stateBbl * k, buyDisc, nbFn);
  return eq * k;
}

// Buy-back: lift the state's crude at its official price × (1 − discount), sell it at the field's realized price (no royalty or
// tax: the state has already taken its share). A trading margin, booked as revenue and crude purchases.
function buyBack(state, fieldId, bbl, disc, nbFn) {
  if (!(bbl > 0)) return;
  const nb = nbFn(state, fieldId, bbl);
  const cost = bbl * Math.max(0, nb.gross) * (1 - disc); // the state's official price for this crude, less the discount
  if (state.company.cash < cost) return;
  spend(state, cost, 'crudePurchases', false);
  earn(state, bbl * nb.gross, 'revenue');
  spend(state, bbl * (nb.transport + nb.region), 'transport', false);
  state.company.stats.bblSold += bbl;
}

export function setBuybackImpl(state, rid, on) {
  if (!regionDef(rid)) return { ok: false, msg: 'No such region.' };
  const s = natStatus(state, rid);
  if (on && s.mode !== 'participation') return { ok: false, msg: 'Buy-back crude is only offered where the state holds a participation share.' };
  econState(state).nat.buyback[rid] = !!on;
  return { ok: true, msg: on ? `You will lift the state’s share of ${regionDef(rid).name} crude at ${Math.round((s.rec.buybackDiscount ?? 0) * 100)}% below spot.` : 'Buy-back stopped.' };
}

// ---- applying a record (news effect or its date) ------------------------------------------------
export function applyNationalization(state, id, { fromNews = false } = {}) {
  const n = natById(id);
  if (!n || !m3On(state)) return null;
  const E = econState(state);
  if (E.nat.applied[n.id] != null) return null;
  if (n.appliesTo === 'us' && !isUsCompany(state)) return null;
  E.nat.applied[n.id] = state.day;
  const rid = n.regionId;
  const name = regionDef(rid)?.name ?? rid;
  const present = hasAssets(state, rid);
  if (n.mode === 'seize') {
    const reg = regionState(state, rid);
    reg.closureSeized = `${n.from}`; // the closure path in tickPolitics must not seize twice
    const r = seizeRegion(state, rid, n.compensation ?? 0, n.reason, { compensationDays: n.compensationDays });
    return r;
  }
  if (n.appliesTo === 'newLicences') {
    if (present) notify(state, 'nationalization', { regionId: rid, mode: n.mode, share: n.share, pause: false, level: 'warn', text: `${n.reason}. Your existing licences in ${name} are untouched; new ones carry a ${Math.round(n.share * 100)}% state share.` });
    return { mode: n.mode };
  }
  if (!present) return { mode: n.mode };
  // compensation for the share taken (participation step-ups) or once (service contracts)
  const y = Math.max(yf(state), n.from);
  const prev = prevShare(state, n, y);
  const delta = n.mode === 'participation' ? Math.max(0, n.share - prev) : Math.max(0, 1 - prev);
  const value = bookValue(state, rid);
  const counsel = Object.values(state.crew).some((c) => c.role === 'counsel' && c.status === 'active') ? 1.2 : 1;
  const comp = value * (n.compensation ?? 0) * delta * counsel;
  const P = politicsState(state);
  if (comp > 0.5) P.compensations.push({ regionId: rid, amount: Math.round(comp * 100) / 100, dueDay: state.day + (n.mode === 'participation' ? PARTICIPATION_COMP_DAYS : SERVICE_COMP_DAYS), reason: n.reason });
  const pct = Math.round((n.share ?? 1) * 100);
  const text = n.mode === 'participation'
    ? `${n.reason}. The state now owns ${pct}% of every barrel you produce in ${name} (and pays ${pct}% of the costs).${comp > 0.5 ? ` Compensation of ${fmt$(comp)} for the share taken.` : ''}`
    : `${n.reason}. In ${name} the oil now belongs to the state; you operate your wells for ${fmt$(serviceFee(state, n))} a barrel.${comp > 0.5 ? ` Compensation ${fmt$(comp)}.` : ''}`;
  notify(state, 'nationalization', { regionId: rid, mode: n.mode, share: n.share ?? 1, compensation: comp, pause: true, level: 'major', text });
  if (n.mode === 'participation' && (n.buybackDiscount ?? 0) > 0) {
    queueDecision(state, {
      kind: 'participation', id: `participation_${n.id}`, regionId: rid, natId: n.id, day: state.day, title: `Participation: ${name}`, text,
      options: [
        { choice: 'buyback', label: `Buy back the state’s crude (${Math.round(n.buybackDiscount * 100)}% below spot)`, desc: 'Lift the barrels you used to own and sell them on: a trading margin, if freight allows.' },
        { choice: 'decline', label: 'Take your share only' },
      ],
    });
  }
  bumpStat(state, 'participationSteps');
  return { mode: n.mode, comp };
}

function prevShare(state, n, y) {
  let s = 0;
  for (const r of NATIONALIZATIONS) {
    if (r.regionId !== n.regionId || r.id === n.id || r.appliesTo) continue;
    if (r.from < n.from && y - 1 / 365 >= r.from && y - 1 / 365 < r.until) s = Math.max(s, r.mode === 'participation' ? r.share : 1);
  }
  return s;
}

export function resolveParticipationImpl(state, d, choice) {
  if (!['buyback', 'decline'].includes(choice)) return { ok: false, msg: 'Choose buyback or decline.' };
  popDecision(state, d.id);
  if (choice === 'buyback') return setBuybackImpl(state, d.regionId, true);
  econState(state).nat.buyback[d.regionId] = false;
  return { ok: true, msg: 'You keep your equity barrels only.' };
}

// Daily: date-driven records (those without a news item, or loaded saves) apply on their `from`.
export function tickNationalizations(state) {
  const y = yf(state);
  const E = econState(state);
  for (const n of NATIONALIZATIONS) {
    if (E.nat.applied[n.id] != null || y < n.from || y >= n.until) continue;
    if (n.mode === 'seize') { E.nat.applied[n.id] = state.day; continue; } // tickPolitics' closure path seizes
    applyNationalization(state, n.id);
  }
  // buy-back switches off where participation ended
  for (const rid of Object.keys(E.nat.buyback)) if (E.nat.buyback[rid] && natStatus(state, rid).mode !== 'participation') E.nat.buyback[rid] = false;
}

// ---- occupation shut-ins and the 1991 Kuwait fires -------------------------------------------------
// M2 already sets regions[rid].occupiedUntil (no leases/spuds). From 1946 an occupation also stops production.
export function occupationShutIns(state) {
  for (const [rid, reg] of Object.entries(state.regions ?? {})) {
    if (!(reg.occupiedUntil > state.day)) continue;
    reg.m3OccStart ??= state.day;
    const until = Math.min(reg.occupiedUntil, reg.m3OccStart + OCCUPATION_SHUTIN_MAX_DAYS);
    if (state.day >= until) continue;
    const fids = new Set(fieldsInRegion(state, rid));
    for (const w of Object.values(state.wells)) if (fids.has(w.fieldId) && w.prod && w.status === 'PRODUCING') w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + 1);
  }
  for (const reg of Object.values(state.regions ?? {})) if (reg.m3OccStart != null && !(reg.occupiedUntil > state.day)) reg.m3OccStart = null;
}

export function applyWellFires(state, e) {
  const E = econState(state);
  const fids = new Set(e.fieldIds ?? fieldsInRegion(state, e.regionId));
  const wells = Object.values(state.wells).filter((w) => fids.has(w.fieldId) && w.prod && ['PRODUCING', 'SHUT_IN'].includes(w.status) && !w.ownerOperatorId);
  const [lo, hi] = e.capDays ?? [30, 250];
  let n = 0;
  for (const w of wells) {
    if (!chance(state, e.fraction ?? 0.8)) continue;
    const until = state.day + int(state, lo, hi);
    w.fire = { day: state.day, untilDay: until, m3: true };
    w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, until);
    E.fires[w.id] = { day: state.day, untilDay: until, fieldId: w.fieldId };
    n++;
  }
  const name = regionDef(e.regionId)?.name ?? e.regionId;
  if (n) notify(state, 'wellFires', { regionId: e.regionId, wells: n, pause: true, level: 'major', text: `${n} of your wells in ${name} were blown up by the retreating army and are burning. Fire-fighting crews will cap them one by one.` });
  // the capping contracts: a reputable driller can sign on with the fire-fighters (achievement Hellfighter)
  if (state.company.reputation >= 40) {
    const wellsToCap = 4 + Math.floor(state.company.reputation / 20);
    const pay = eraDollars(state, FIRE_CAP_COST, FIRE_CAP_BASE_YEAR);
    queueDecision(state, {
      kind: 'wellFireContract', id: `wellfires_${e.regionId}_${state.day}`, regionId: e.regionId, day: state.day, wells: wellsToCap, pay,
      title: `${name}: the oil fires`, text: `Kuwait is hiring every well-control crew in the world to cap more than 700 burning wells. Sign on for ${wellsToCap} wells at ${fmt$(pay)} each?`,
      options: [
        { choice: 'accept', label: `Send a crew (${wellsToCap} wells)`, desc: 'Weeks of heat, smoke and mines; each capped well pays on completion.' },
        { choice: 'decline', label: 'Decline' },
      ],
    });
  }
  return n;
}

export function resolveWellFireContractImpl(state, d, choice) {
  if (!['accept', 'decline'].includes(choice)) return { ok: false, msg: 'Choose accept or decline.' };
  popDecision(state, d.id);
  if (choice === 'decline') return { ok: true, msg: 'You leave the fires to Red Adair.' };
  const E = econState(state);
  E.fireContracts.push({ id: d.id, regionId: d.regionId, left: d.wells, pay: d.pay, nextDay: state.day + int(state, 10, 30) });
  return { ok: true, msg: `Your crew flies to Kuwait to cap ${d.wells} wells.` };
}

export function tickWellFires(state) {
  const E = state.m3econ;
  if (!E) return;
  for (const [wid, f] of Object.entries(E.fires)) {
    const w = state.wells[wid];
    if (!w || state.day >= f.untilDay) {
      if (w?.fire?.m3) w.fire = null;
      delete E.fires[wid];
      if (w) {
        const pay = eraDollars(state, FIRE_CAP_COST, FIRE_CAP_BASE_YEAR);
        spend(state, pay, 'other', false);
        bumpStat(state, 'wellFiresCapped');
        notify(state, 'news', { kind: 'security', fieldId: w.fieldId, wellId: w.id, text: `${w.name} is capped (${fmt$(pay)} to the fire-fighters).` });
      }
    }
  }
  for (const c of E.fireContracts) {
    if (c.left <= 0 || state.day < c.nextDay) continue;
    c.left--;
    c.nextDay = state.day + int(state, 10, 30);
    earn(state, c.pay, 'other');
    bumpStat(state, 'wellFiresCapped');
    if (chance(state, 0.03)) state.company.reputation = Math.min(100, state.company.reputation + range(state, 1, 3));
    notify(state, 'news', { kind: 'security', regionId: c.regionId, text: `Your crew capped another burning well (${fmt$(c.pay)}). ${c.left} to go.` });
  }
  E.fireContracts = E.fireContracts.filter((c) => c.left > 0);
}

export function nationalizationView(state, rid) {
  const y = yf(state);
  const s = natStatus(state, rid);
  return {
    regionId: rid, mode: s.mode, share: s.share, record: s.rec ? { ...s.rec } : null,
    fee: s.mode === 'service' ? serviceFee(state, s.rec) : null,
    buyback: { on: !!state.m3econ?.nat?.buyback?.[rid], discount: s.rec?.buybackDiscount ?? 0, available: s.mode === 'participation' && (s.rec?.buybackDiscount ?? 0) > 0 },
    applied: NATIONALIZATIONS.filter((n) => n.regionId === rid && state.m3econ?.nat?.applied?.[n.id] != null).map((n) => ({ id: n.id, mode: n.mode, share: n.share ?? 1, day: state.m3econ.nat.applied[n.id] })),
    upcoming: NATIONALIZATIONS.filter((n) => n.regionId === rid && n.from > y).map((n) => ({ id: n.id, mode: n.mode, from: n.from })).slice(0, 0), // history is not foretold
    newLicenceShare: NATIONALIZATIONS.filter((n) => n.regionId === rid && n.appliesTo === 'newLicences' && y >= n.from && y < n.until).reduce((m, n) => Math.max(m, n.share), 0),
  };
}
